import { Auth } from "firebase/auth";
import { doc, Firestore, getDoc, setDoc } from "firebase/firestore";
import debounce from "lodash/debounce";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "reactfire";
import { useSettingsSyncEnabled } from "../../hooks/config/useSettingsSyncEnabled";
import { setCampgroundsVisible } from "../../store/slices/campgroundLayer";
import { setBasemap } from "../../store/slices/mapSettings";
import { createLogger } from "../../utils/logger";

const logger = createLogger("SettingsProvider");
import {
  setPublicLandsVisible,
  setSelectedAgencies,
} from "../../store/slices/publicLandsLayer";
import {
  setSyncWithFilter,
  setWeatherColorRamp,
  setWeatherLayerType,
  setWeatherMonth,
  setWeatherOpacity,
  setWeatherShowTooltip,
  setWeatherUnits,
  setWeatherVisible,
} from "../../store/slices/weatherLayer";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { SettingsContext } from "./SettingsContext";
import { UserSettings } from "./types";

const SETTINGS_DEBOUNCE_MS = 1000; // Wait 1 second after last change before syncing

export const SettingsProvider = ({
  auth,
  firestore,
  children,
}: {
  auth: Auth;
  firestore: Firestore;
  children: JSX.Element;
}) => {
  const { data: authUser } = useUser();
  const dispatch = useAppDispatch();
  const settingsSyncEnabled = useSettingsSyncEnabled();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(false);

  // Track if we've done the initial migration for this session
  const hasMigratedRef = useRef(false);

  // Select the persisted Redux state
  const mapSettings = useAppSelector((state) => state.mapSettings);
  const publicLandsLayer = useAppSelector((state) => state.publicLandsLayer);
  const campgroundLayer = useAppSelector((state) => state.campgroundLayer);
  const weatherLayer = useAppSelector((state) => state.weatherLayer);

  // Get Firestore document reference
  const getSettingsDocRef = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;
    return doc(firestore, "users", userId, "settings", "preferences");
  }, [auth, firestore]);

  // Load settings from Firestore
  const loadFromFirestore = useCallback(async () => {
    const docRef = getSettingsDocRef();
    if (!docRef) return;

    setLoading(true);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data() as UserSettings;
        setSettings(data);
        return data;
      }
      return null;
    } catch (error) {
      logger.error("Failed to load settings from Firestore:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getSettingsDocRef]);

  // Sync current Redux state to Firestore
  const syncToFirestore = useCallback(async () => {
    const docRef = getSettingsDocRef();
    if (!docRef) return;

    try {
      // Note: weatherLayer.loading is excluded from sync
      // as it's a transient UI state, not a user preference
      const settingsData: UserSettings = {
        mapSettings: {
          basemap: mapSettings.basemap,
        },
        publicLandsLayer: {
          visible: publicLandsLayer.visible,
          selectedAgencies: publicLandsLayer.selectedAgencies,
        },
        campgroundLayer: {
          visible: campgroundLayer.visible,
        },
        weatherLayer: {
          visible: weatherLayer.visible,
          layer: weatherLayer.layer,
          month: weatherLayer.month,
          opacity: weatherLayer.opacity,
          colorRamp: weatherLayer.colorRamp,
          showTooltip: weatherLayer.showTooltip,
          units: weatherLayer.units,
          syncWithFilter: weatherLayer.syncWithFilter,
        },
        lastUpdated: new Date(),
      };

      await setDoc(docRef, settingsData);
      setSettings(settingsData);
    } catch (error) {
      logger.error("Failed to sync settings to Firestore:", error);
    }
  }, [
    getSettingsDocRef,
    mapSettings,
    publicLandsLayer,
    campgroundLayer,
    weatherLayer,
  ]);

  // Debounced version of syncToFirestore
  const debouncedSync = useMemo(
    () => debounce(syncToFirestore, SETTINGS_DEBOUNCE_MS),
    [syncToFirestore]
  );

  // Handle user login: load settings from Firestore and migrate if needed
  useEffect(() => {
    // Only sync if feature flag is enabled
    if (!settingsSyncEnabled) return;
    if (!authUser?.uid || hasMigratedRef.current) return;

    const migrateSettings = async () => {
      hasMigratedRef.current = true;

      // Load settings from Firestore
      const firestoreSettings = await loadFromFirestore();

      // If Firestore doesn't have settings, upload current localStorage state
      if (!firestoreSettings) {
        logger.log("No settings in Firestore, migrating localStorage settings");
        await syncToFirestore();
      } else {
        logger.log("Loaded settings from Firestore, applying to Redux");

        // Dispatch actions to update Redux state with Firestore settings
        // This ensures settings sync across browsers

        // Map settings
        dispatch(setBasemap(firestoreSettings.mapSettings.basemap));

        // Public lands layer
        dispatch(setPublicLandsVisible(firestoreSettings.publicLandsLayer.visible));
        dispatch(setSelectedAgencies(firestoreSettings.publicLandsLayer.selectedAgencies));

        // Campground layer
        dispatch(setCampgroundsVisible(firestoreSettings.campgroundLayer.visible));

        // Weather layer
        dispatch(setWeatherVisible(firestoreSettings.weatherLayer.visible));
        dispatch(setWeatherLayerType(firestoreSettings.weatherLayer.layer));
        dispatch(setWeatherMonth(firestoreSettings.weatherLayer.month));
        dispatch(setWeatherOpacity(firestoreSettings.weatherLayer.opacity));
        dispatch(setWeatherColorRamp(firestoreSettings.weatherLayer.colorRamp));
        dispatch(setWeatherShowTooltip(firestoreSettings.weatherLayer.showTooltip));
        dispatch(setWeatherUnits(firestoreSettings.weatherLayer.units));
        dispatch(setSyncWithFilter(firestoreSettings.weatherLayer.syncWithFilter));
      }
    };

    migrateSettings();
  }, [settingsSyncEnabled, authUser?.uid, loadFromFirestore, syncToFirestore, dispatch]);

  // Reset migration flag when user logs out
  useEffect(() => {
    if (!authUser?.uid) {
      hasMigratedRef.current = false;
      setSettings(null);
    }
  }, [authUser?.uid]);

  // Sync to Firestore when Redux state changes (debounced)
  useEffect(() => {
    // Only sync if feature flag is enabled, user is logged in, and we've completed initial migration
    if (!settingsSyncEnabled) return;
    if (!authUser?.uid || !hasMigratedRef.current) return;

    debouncedSync();

    // Cleanup: cancel any pending debounced calls
    return () => {
      debouncedSync.cancel();
    };
  }, [
    settingsSyncEnabled,
    authUser?.uid,
    mapSettings,
    publicLandsLayer,
    campgroundLayer,
    weatherLayer,
    debouncedSync,
  ]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        syncToFirestore,
        loadFromFirestore,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
